"use client"

import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import Link from "next/link"
import { Copy, Check } from "lucide-react"
import { createContext, useContext, useRef, useState, type ReactNode } from "react"
import { cn } from "@/lib/utils"

/**
 * Published by the <pre> renderer so the <code> renderer can tell a fenced
 * block from inline code.
 *
 * WHY: the previous implementation inferred "inline" from `!className`. A fence
 * opened with a bare ``` carries no `language-*` class, so every untagged code
 * block was classified as inline and rendered as a single run-on line with the
 * newlines collapsed. Only the presence of a <pre> ancestor is a reliable
 * signal, and only <pre> can supply it.
 */
const InsidePreContext = createContext(false)

function CopyButton({ getText }: { getText: () => string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(getText())
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      onClick={handleCopy}
      className="absolute top-2 end-2 p-1.5 rounded-md bg-background/80 hover:bg-background border border-border text-muted-foreground hover:text-foreground transition-colors"
      title="Copy code"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  )
}

/** Renders every fenced block — tagged or untagged. */
export function MarkdownCodeBlock({ children }: { children?: ReactNode }) {
  const preRef = useRef<HTMLPreElement>(null)

  return (
    <div dir="ltr" className="relative group my-2 max-w-full overflow-hidden">
      <CopyButton getText={() => preRef.current?.textContent ?? ""} />
      <pre
        ref={preRef}
        className="overflow-x-auto whitespace-pre rounded-md bg-muted/70 p-4 text-sm max-w-full"
      >
        <InsidePreContext.Provider value={true}>{children}</InsidePreContext.Provider>
      </pre>
    </div>
  )
}

export function MarkdownCode({
  className,
  children,
  ...props
}: {
  className?: string
  children?: ReactNode
} & React.HTMLAttributes<HTMLElement>) {
  const insidePre = useContext(InsidePreContext)

  if (insidePre) {
    return (
      <code className={cn("font-mono", className)} {...props}>
        {children}
      </code>
    )
  }

  return (
    <code
      className={cn("px-1.5 py-0.5 rounded-md bg-muted text-sm font-mono break-all", className)}
      {...props}
    >
      {children}
    </code>
  )
}

export function MarkdownRenderer({
  content,
  className,
  renderLink,
}: {
  content: string
  className?: string
  /**
   * Claims a link before the default rendering: return an element to render it
   * yourself, or null to leave it to the renderer. The knowledge assistant uses
   * it to turn its citation markers into numbered source chips.
   */
  renderLink?: (href: string | undefined, children: ReactNode) => ReactNode | null
}) {
  return (
    // `hf-prose` is the hook globals.css uses to undo Tailwind Typography's
    // ::before/::after backticks around inline <code> — the renderer already
    // styles inline code as a pill, so the literal backticks read as content.
    <div
      className={cn(
        "hf-prose prose prose-sm dark:prose-invert max-w-none overflow-hidden break-words [overflow-wrap:anywhere]",
        className,
      )}
    >
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        table: ({ children }) => (
          <div className="overflow-x-auto my-2 rounded-md border border-border max-w-full">
            <table className="w-full text-sm">{children}</table>
          </div>
        ),
        thead: ({ children }) => (
          <thead className="bg-muted/50 border-b border-border">{children}</thead>
        ),
        th: ({ children }) => (
          <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="px-3 py-2 text-sm border-t border-border">{children}</td>
        ),
        code: MarkdownCode,
        pre: MarkdownCodeBlock,
        a: ({ children, href }) => {
          const claimed = renderLink?.(href, children)
          if (claimed !== undefined && claimed !== null) return <>{claimed}</>
          // An in-app path must navigate CLIENT-SIDE in this tab. The chat
          // companion lives in the root layout, so a client transition carries
          // the open conversation to the destination — which is the whole
          // point of linking an entity. `target="_blank"` (the old universal
          // behaviour) opened a second tab with a cold-booted, empty
          // companion; a plain <a> would reload the SPA and do the same in
          // this one.
          //
          // `//host/path` is protocol-relative and goes OFF-SITE despite the
          // leading slash, so a bare startsWith('/') would hand an external
          // origin to the client router.
          const internal =
            typeof href === "string" && href.startsWith("/") && !href.startsWith("//")
          const className =
            "text-primary underline underline-offset-2 hover:text-primary/80"
          return internal ? (
            <Link href={href} className={className}>
              {children}
            </Link>
          ) : (
            <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
              {children}
            </a>
          )
        },
        ul: ({ children }) => <ul className="list-disc list-inside space-y-1 my-2">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 my-2">{children}</ol>,
        li: ({ children }) => <li className="text-sm">{children}</li>,
        p: ({ children }) => <p className="my-1.5 leading-relaxed">{children}</p>,
        h1: ({ children }) => <h1 className="text-lg font-bold mt-4 mb-2">{children}</h1>,
        h2: ({ children }) => <h2 className="text-base font-semibold mt-3 mb-1.5">{children}</h2>,
        h3: ({ children }) => <h3 className="text-sm font-semibold mt-2 mb-1">{children}</h3>,
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        blockquote: ({ children }) => (
          <blockquote className="border-s-2 border-primary/30 ps-3 my-2 text-muted-foreground italic">
            {children}
          </blockquote>
        ),
        hr: () => <hr className="my-3 border-border" />,
      }}
    >
      {content}
    </ReactMarkdown>
    </div>
  )
}
